import classNames from 'classnames'
import {
  isEmptyDocument,
  type StructuredText as StructuredTextType
} from 'datocms-structured-text-utils'
import { graphql } from 'gatsby'
import { IGatsbyImageData } from 'gatsby-plugin-image'
import React, { type ReactElement } from 'react'
import { isMobile } from 'react-device-detect'
import { Gallery } from 'react-photoswipe-gallery'
import ReactPlayer from 'react-player'

import Ctas from '@/components/blocks-library/cta/ctas'
import Image from '@/components/image/image'
import { ImageViewModal } from '@/components/image/image-view-modal'
import Link from '@/components/link/link'
import StructuredTextDefault from '@/components/structured-text/structured-text-default'
import Player from '@/components/video/player/player'
import { ViewModal } from '@/components/view-modal/view-modal'
import { resolveCtaUrl } from '@/internal/utils/resolve-cta-link-url'
import { prepareVideoThumbnailUrl } from '@/utils/thumbnail-url'

import * as styles from './featured-static.module.scss'

export interface Logo {
  originalId: string
  gatsbyImageData: IGatsbyImageData
  title: string
  alt: string
  customData?: Record<string, string>
  height: number
  width: number
}

export const FeaturedStatic = (
  props: Queries.FeaturedStaticFragment
): ReactElement => {
  const {
    ctas,
    widthFirstColumn,
    reverse,
    title,
    subtitle,
    content,
    hideImageResponsive,
    mediaViewInModal,
    image,
    oneColumn,
    fullImage,
    video,
    oneColumnCentered
  } = props
  const mainLink = ctas?.[0] ? resolveCtaUrl(ctas[0]) : ''
  const firstColumn: string = widthFirstColumn?.toString() ?? '6'
  const order = reverse !== null && reverse ? 'order-lg-1' : ''
  const isEmptyContent = isEmptyDocument(
    (content as Record<string, unknown> | null)?.value
  )
  const hideOnTablet =
    hideImageResponsive !== null && hideImageResponsive
      ? 'd-none d-md-block'
      : ''
  const viewInModal = !!(mediaViewInModal !== null && mediaViewInModal)
  const videoUrl = video?.url ?? undefined
  const imageOnly = image !== null && videoUrl === undefined
  const textAlignment = (
    oneColumnCentered: boolean | null | undefined,
    oneColumn: boolean
  ) => {
    if (
      oneColumn &&
      (oneColumnCentered === null ||
        oneColumnCentered === undefined ||
        oneColumnCentered === true)
    ) {
      return 'text-center'
    }
    return 'text-start'
  }
  return (
    <div
      className={`container ${styles.textImage} ${oneColumn !== null ? styles.sectionOneColumn : ''}`}
    >
      <div className="row">
        {oneColumn === true && (
          <div
            className={classNames(
              'col-12',
              textAlignment(oneColumnCentered, oneColumn),
              styles.colText,
              styles.oneColumn
            )}
          >
            <FeaturedMedia
              imageOnly={imageOnly}
              fullImage={fullImage}
              image={image}
              mainLink={mainLink}
              video={video}
              videoUrl={videoUrl}
              viewInModal={viewInModal}
            />
            <Content
              title={title}
              subtitle={subtitle}
              isEmptyContent={isEmptyContent}
              content={content}
              ctas={ctas}
              centered={true}
            />
          </div>
        )}

        {(oneColumn === false ||
          oneColumn == undefined ||
          oneColumn == null) && (
          <>
            <div
              className={classNames(
                'col-12',
                `col-lg-${firstColumn}`,
                'pe-4 ps-3',
                order,
                styles.colText
              )}
            >
              <Content
                title={title}
                subtitle={subtitle}
                isEmptyContent={isEmptyContent}
                content={content}
                ctas={ctas}
                centered={false}
              />
            </div>
            <div className={classNames('col ps-3', hideOnTablet)}>
              <FeaturedMedia
                imageOnly={imageOnly}
                fullImage={fullImage}
                image={image}
                mainLink={mainLink}
                video={video}
                videoUrl={videoUrl}
                viewInModal={viewInModal}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

type ContentProps = {
  title: string | null
  subtitle: string | null
  isEmptyContent: boolean
  content: Queries.FeaturedStaticFragment['content']
  ctas: Queries.FeaturedStaticFragment['ctas']
  centered: boolean
}
const Content: React.FC<ContentProps> = ({
  title,
  subtitle,
  isEmptyContent,
  content,
  ctas,
  centered
}) => {
  return (
    <>
      {title !== '' && <h2 dangerouslySetInnerHTML={{ __html: title }} />}
      {subtitle !== '' && <h3>{subtitle}</h3>}
      {!isEmptyContent && (
        <div className="pb-2">
          <StructuredTextDefault
            content={content as unknown as StructuredTextType}
          />
        </div>
      )}
      {ctas !== null && ctas.length > 0 && (
        <div
          className={classNames(`mt-4 text-center`, styles.links, {
            'text-lg-start': !centered
          })}
        >
          <Ctas items={ctas} />
        </div>
      )}
    </>
  )
}

type FeaturedMediaProps = {
  imageOnly: boolean
  videoUrl: string | undefined
  mainLink: string
  image: Queries.FeaturedStaticFragment['image']
  video: Queries.FeaturedStaticFragment['video']
  fullImage: Queries.FeaturedStaticFragment['fullImage']
  viewInModal: Queries.FeaturedStaticFragment['mediaViewInModal']
}
const FeaturedMedia: React.FC<FeaturedMediaProps> = ({
  imageOnly,
  videoUrl,
  mainLink,
  image,
  video,
  fullImage,
  viewInModal
}) => {
  if (imageOnly && mainLink !== '') {
    return (
      <Link to={mainLink} className={styles.imageLink}>
        <ImageElement
          image={image}
          fullImage={fullImage}
          viewInModal={viewInModal}
        />
      </Link>
    )
  }
  if (imageOnly && mainLink === '') {
    return (
      <ImageElement
        image={image}
        fullImage={fullImage}
        viewInModal={viewInModal}
      />
    )
  }

  if (videoUrl) {
    return (
      <VideoElement video={video} image={image} viewInModal={viewInModal} />
    )
  }
  return <></>
}
export default FeaturedStatic

type VideoProps = {
  video: Queries.FeaturedStaticFragment['video']
  image: Queries.FeaturedStaticFragment['image']
  viewInModal: Queries.FeaturedStaticFragment['mediaViewInModal']
}

const VideoElement: React.FC<VideoProps> = ({ video, image, viewInModal }) => {
  const src = image !== null ? image.fluid?.src : (video?.thumbnailUrl ?? '')
  const thumbnail = prepareVideoThumbnailUrl(src)
  if (viewInModal === true && !isMobile) {
    const alt = video?.title ?? ''
    return (
      <Gallery>
        <ViewModal
          content={
            <ReactPlayer
              width={'100%'}
              height={'100%'}
              style={{ maxWidth: '950px' }} /** Prevent toolbar disappear  */
              url={video?.url ?? ''}
              config={{
                youtube: {
                  playerVars: { showinfo: 0, rel: 0 }
                },
                vimeo: {
                  playerOptions: { vimeo_logo: false }
                }
              }}
              controls={true}
              playing={true}
              pip={false}
            />
          }
          preview={
            <span className={styles.playVideo}>
              <img
                src={thumbnail}
                alt={alt}
                style={{
                  cursor: 'pointer',
                  objectFit: 'contain',
                  width: '100%',
                  maxHeight: '100%'
                }}
              />
            </span>
          }
        />
      </Gallery>
    )
  }
  return <Player video={video?.url} autoPlay={!!src} preview={thumbnail} />
}
type ImageProps = {
  image: Queries.FeaturedStaticFragment['image']
  fullImage: Queries.FeaturedStaticFragment['fullImage']
  viewInModal: Queries.FeaturedStaticFragment['mediaViewInModal']
}
const ImageElement: React.FC<ImageProps> = ({
  image,
  fullImage,
  viewInModal
}) => {
  if (viewInModal === true) {
    return (
      <Gallery>
        <ImageViewModal image={image} fullImage={fullImage} />
      </Gallery>
    )
  }
  return <Image image={image} alt="" />
}

export const query = graphql`
  fragment FeaturedStatic on DatoCmsFeaturedStatic {
    __typename
    id
    navigationLabel
    title
    subtitle
    content {
      value
      blocks {
        __typename
        ... on DatoCmsVideo {
          ...VideoInline
        }
        ... on DatoCmsImage {
          ...ImageInline
        }
        ... on DatoCmsEmbed {
          ...EmbedInline
        }
        ... on DatoCmsCta {
          # All Blocks insert in a structured text needs to alias originalId to id
          id: originalId
          ...Cta
        }
      }
      links {
        __typename
        ... on DatoCmsPctDialog {
          ...PctDialogCardInline
        }
        ... on DatoCmsPctTopic {
          ...PctTopicCardInline
        }
        ... on DatoCmsPctPaper {
          ...PctPaperCardInline
        }
        ... on DatoCmsPctInternalPage {
          ...PctInternalPageCardInline
        }
        ... on DatoCmsPctPost {
          ...PctPostCardInline
        }
        ... on DatoCmsPctProject {
          ...PctProjectCardInline
        }
        ... on DatoCmsPctPage {
          ...PctPageCardInline
        }
        ... on DatoCmsPctTag {
          ...PctTagCardInline
        }
        ... on DatoCmsPctReport {
          ...PctReportCardInline
        }
        ... on DatoCmsPctLink {
          ...PctLink
        }
        ... on DatoCmsGctPerson {
          ...PctReportPersonInline
        }
      }
    }
    ctas {
      __typename
      ... on DatoCmsCta {
        ...Cta
      }
    }
    image {
      alt
      originalId
      gatsbyImageData(imgixParams: { w: "1200" })
      fluid(imgixParams: { w: "1200" }) {
        src
        height
        width
      }
    }
    fullImage: image {
      alt
      originalId
      gatsbyImageData(imgixParams: { w: "1600" })
      fluid(imgixParams: { w: "1600" }) {
        src
        srcSet
        height
        width
      }
    }
    video {
      url
      thumbnailUrl
      title
    }
    hideImageResponsive
    reverse
    oneColumn
    mediaViewInModal
    widthFirstColumn
    oneColumnCentered
  }
`
